// src/contracts/Contract.js
// Main contract editor page. Mounted at /billing/create-contract (we keep that
// URL because it sits in the Billing nav, but the code lives here in /contracts).
//
// Flow:
//   1) Pick a project → owner + jobsite + PO# autofill
//   2) Pick contract type (HIC live; TI/SM/SF disabled)
//   3) First edit creates a Draft Contract record in MongoDB
//   4) Subsequent edits debounced-save to the DB
//   5) Generate PDF & mark sent → backend renders PDF + uploads to Dropbox
//   6) Upload Signed Copy → backend stores the signed PDF + flips status

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Undo2, Redo2 } from 'lucide-react';

import api from './api';
import HICTemplate from './templates/HICTemplate';
import { EditableHeading, RegulatedBadge } from './components/ProseBlock';
import HeaderSection, { SectionControls } from './components/HeaderSection';
import ContractTableEditor from './components/ContractTableEditor';
import ExhibitsPanel from './components/ExhibitsPanel';
import ExhibitDetail from './components/ExhibitDetail';
import ContractTypeDropdown from './components/ContractTypeDropdown';
import LifecycleControls from './components/LifecycleControls';
import StatusPill from './components/StatusPill';

// ----- helpers -----
// localStorage key for the "currently editing" contract pointer. The full draft
// content lives in MongoDB — this key only remembers WHICH draft we last opened
// so navigating away and back doesn't dump you into a blank page.
const DRAFT_KEY = 'activeContractDraftId';

function letterOf(i) { return String.fromCharCode(65 + i); }
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fileNameFromPath(p) {
  if (!p) return null;
  return p.split('/').pop();
}

// ---- Backward-compat migration ----
// Drafts saved before the section-model refactor used types 'prose' / 'bullets'
// with paragraphs[] / items[] arrays. The new HeaderSection only renders
// type === 'header' with an html blob. This converts old shapes on load so
// pre-refactor drafts still display (and re-save in the new format on edit).
function _escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
// Old paragraphs contained inline {ref:exhibitId} tokens — convert to span markup
function _refTokensToSpans(s) {
  return _escapeHtml(s).replace(/\{ref:([^}]+)\}/g, (_, id) =>
    `<span data-ref="${id}">EXHIBIT ?</span>`
  );
}
function migrateSection(sec) {
  if (!sec || !sec.type) return sec;
  if (sec.type === 'prose') {
    const html = (sec.paragraphs || []).map(p => `<p>${_refTokensToSpans(p)}</p>`).join('');
    return { id: sec.id, type: 'header', heading: sec.heading || '', html, regulated: !!sec.regulated };
  }
  if (sec.type === 'bullets') {
    const lis = (sec.items || []).map(it => `<li>${_refTokensToSpans(it)}</li>`).join('');
    const html = `<ul>${lis}</ul>`;
    return { id: sec.id, type: 'header', heading: sec.heading || '', html, regulated: !!sec.regulated };
  }
  return sec; // header / table pass through
}
function migrateFormData(fd) {
  if (!fd || !Array.isArray(fd.sections)) return fd || { sections: [] };
  return { ...fd, sections: fd.sections.map(migrateSection) };
}
// Render a Date as "MM/DD/YYYY · h:mm:ss AM/PM" so timestamps in the header
// and on existing-version pills always include the date — distinguishes
// multiple drafts saved on the same day, and tells you at-a-glance if the
// last save was today vs. yesterday.
function formatStamp(d) {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt)) return '';
  return `${dt.toLocaleDateString('en-US')} · ${dt.toLocaleTimeString('en-US')}`;
}
function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// Tiny debounce hook
function useDebouncedEffect(effect, deps, delay) {
  const cb = useRef(effect);
  cb.current = effect;
  useEffect(() => {
    const t = setTimeout(() => cb.current(), delay);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export default function Contract() {
  const navigate = useNavigate();

  // ---- top-of-form state ----
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [company, setCompany] = useState(null);

  // selectedProject._id is the authoritative project id everywhere downstream.
  // We derive customer/jobsite/etc. from selectedProject (it's populated server-side
  // in /projects, matching how CreateInvoice / CreateQuote use it).
  const projectId = selectedProject?._id || '';
  const project = selectedProject;
  const customer = selectedProject?.customer || null;

  const [contractType, setContractType] = useState('HIC');
  const [version, setVersion] = useState('1');
  const [effectiveDate, setEffectiveDate] = useState(todayISO());
  const [contractPrice, setContractPrice] = useState(0);

  // ---- contract document state ----
  const [contractId, setContractId] = useState(null);
  // _setFormData / _setExhibits are the raw setters. Used directly by:
  //   - bootstrap restore (loading from DB)
  //   - project change template seed
  //   - undo / redo (which manage history themselves)
  //   - hard resets (X-button on project picker, discard)
  // Component code should use the wrapped setFormData / setExhibits below,
  // which push the previous state onto the undo stack before applying changes.
  const [formData,  _setFormData] = useState({ sections: [] });
  const [exhibits,  _setExhibits] = useState([]);
  const [status, setStatus] = useState('Draft');

  // ---- which view is active in the central column ----
  // 'body' = main contract sections; otherwise an exhibitId string.
  // Declared up here (before the undo/redo block) because the fallback
  // useEffect below references activeView; declaring it later would put
  // the dep array in temporal-dead-zone at render time.
  const [activeView, setActiveView] = useState('body');

  // ---- universal undo / redo ----
  // History snapshots are plain JSON, capped at HISTORY_MAX entries each side.
  const HISTORY_MAX = 50;
  const [history, setHistory] = useState({ past: [], future: [] });

  // If the active exhibit gets removed (by trash, reorder-and-delete, etc.),
  // snap the central view back to the contract body so we don't try to render
  // an editor for a now-missing exhibit.
  useEffect(() => {
    if (activeView !== 'body' && !exhibits.find(e => e.exhibitId === activeView)) {
      setActiveView('body');
    }
  }, [exhibits, activeView]);

  // ---- Scroll position + activeView memory (per contract) ----
  // localStorage key uses the contract id so each draft has its own slot.
  // Saves on debounced scroll, and once on unmount in case the user navigates
  // away within the debounce window. Restores after the contract has loaded
  // (waits two animation frames so sections have rendered their full height).
  const SCROLL_KEY = (id) => `contractView_${id}`;
  const restoredForRef = useRef(null);

  // ---- Page scroll container ----
  // The page scrolls inside its own container (the content area below the top
  // bar) rather than the window. The global app shell wraps pages in an
  // overflow-hidden box, which prevents a `sticky`/`fixed` header from behaving
  // cleanly; making the body scroll internally lets the top bar be an ordinary,
  // non-scrolling flex child that content can never slip above or behind.
  const scrollRef = useRef(null);

  // Hide the page while we're restoring scroll, so the user doesn't see a
  // flash of "rendered at top → jump to saved position". Initialized
  // synchronously from localStorage at mount so the first render is already
  // invisible if there's a scroll to restore. Set to false once the restore
  // succeeds, or after a safety timeout, or if there was nothing to restore.
  const [isRestoringScroll, setIsRestoringScroll] = useState(() => {
    try {
      const id = localStorage.getItem('activeContractDraftId');
      if (!id) return false;
      const raw = localStorage.getItem(`contractView_${id}`);
      if (!raw) return false;
      const { scrollY } = JSON.parse(raw);
      return typeof scrollY === 'number' && scrollY > 0;
    } catch (_) { return false; }
  });

  // Safety net: never keep the page hidden for longer than 2.5 seconds
  // (in case bootstrap fails or the page never gets tall enough)
  useEffect(() => {
    if (!isRestoringScroll) return;
    const t = setTimeout(() => setIsRestoringScroll(false), 2500);
    return () => clearTimeout(t);
  }, [isRestoringScroll]);

  // Restore — fires when contractId becomes truthy or changes.
  //
  // Strategy: poll up to 5 seconds. Each tick, attempt the scroll. Re-check
  // window.scrollY afterward — if the browser clamped us (because the page
  // isn't tall enough YET), retry. Bail out if the user starts interacting,
  // so we don't fight a deliberate scroll.
  //
  // Also pre-emptively turn off the browser's own scroll restoration so
  // it can't snap us back to top after we've restored.
  useEffect(() => {
    if (!contractId) return;
    if (restoredForRef.current === contractId) return; // only restore once per contract
    restoredForRef.current = contractId;
    const raw = localStorage.getItem(SCROLL_KEY(contractId));
    if (!raw) return;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    let cancelled = false;
    let userInterrupted = false;
    const onInterrupt = () => { userInterrupted = true; };
    // Treat actual user gestures as "stop trying" — but a programmatic scroll
    // (which is what we're doing) won't trigger these.
    window.addEventListener('wheel', onInterrupt, { passive: true, once: true });
    window.addEventListener('touchstart', onInterrupt, { passive: true, once: true });
    window.addEventListener('mousedown', onInterrupt, { passive: true, once: true });
    window.addEventListener('keydown', onInterrupt, { passive: true, once: true });

    try {
      const { scrollY, activeView: savedView } = JSON.parse(raw);
      if (savedView && savedView !== 'body') setActiveView(savedView);
      if (typeof scrollY !== 'number' || scrollY <= 0) {
        setIsRestoringScroll(false); // nothing to restore — reveal immediately
        return;
      }

      let attempts = 0;
      const MAX_ATTEMPTS = 100; // 5 seconds at 50ms intervals
      const tryScroll = () => {
        if (cancelled || userInterrupted) {
          setIsRestoringScroll(false);
          return;
        }
        const el = scrollRef.current;
        if (el) el.scrollTop = scrollY;
        // If the container isn't tall enough yet (content still rendering), retry
        if ((!el || Math.abs(el.scrollTop - scrollY) > 5) && attempts < MAX_ATTEMPTS) {
          attempts++;
          setTimeout(tryScroll, 50);
        } else {
          // Either we landed at the target, or we gave up after MAX_ATTEMPTS —
          // either way, reveal the page now
          setIsRestoringScroll(false);
        }
      };
      // Two RAFs lets the initial mount + paint happen
      requestAnimationFrame(() => requestAnimationFrame(tryScroll));
    } catch (_) {
      // Corrupt entry; reveal so we don't leave the page hidden forever
      setIsRestoringScroll(false);
    }

    return () => {
      cancelled = true;
      window.removeEventListener('wheel', onInterrupt);
      window.removeEventListener('touchstart', onInterrupt);
      window.removeEventListener('mousedown', onInterrupt);
      window.removeEventListener('keydown', onInterrupt);
    };
  }, [contractId]);

  // Save — debounced scroll listener + flush on unmount AND on pagehide
  // (covers tab close, mobile Safari back gesture, route changes).
  useEffect(() => {
    if (!contractId) return;
    const el = scrollRef.current;
    let timer = null;
    const write = () => {
      // Never overwrite a stored position with 0. Several paths fire write()
      // around route changes (cleanup, pagehide, etc.) — and at those moments
      // scrollTop can briefly read 0, which would wipe our actual saved
      // position. If you explicitly scroll to the top, you'll just land back
      // at your last non-top position on return; small price.
      const y = el ? el.scrollTop : 0;
      if (y <= 0) return;
      try {
        localStorage.setItem(SCROLL_KEY(contractId), JSON.stringify({
          scrollY: y,
          activeView,
        }));
      } catch (_) { /* quota or private mode; ignore */ }
    };
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(write, 200);
    };
    if (el) el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', write);
    return () => {
      if (el) el.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', write);
      clearTimeout(timer);
      write(); // flush one final time so we don't lose the last sub-200ms
    };
  }, [contractId, activeView]);

  // Tracks whether the user has made any change beyond the template seed.
  // Used by autosave so picking a project (which seeds the template) doesn't
  // itself create a Mongo draft — the draft only materializes once the user
  // actually edits something.
  const hasUserEditedRef = useRef(false);

  const pushHistoryAndSet = (kind, next) => {
    // Any call through the wrapped setters is a user-initiated change.
    hasUserEditedRef.current = true;
    setHistory(h => ({
      past: [...h.past, { formData, exhibits }].slice(-HISTORY_MAX),
      future: [],
    }));
    if (kind === 'formData') {
      _setFormData(typeof next === 'function' ? next(formData) : next);
    } else {
      _setExhibits(typeof next === 'function' ? next(exhibits) : next);
    }
  };
  const setFormData = (next) => pushHistoryAndSet('formData', next);
  const setExhibits = (next) => pushHistoryAndSet('exhibits', next);

  const undo = () => {
    setHistory(h => {
      if (!h.past.length) return h;
      const prev = h.past[h.past.length - 1];
      _setFormData(prev.formData);
      _setExhibits(prev.exhibits);
      return {
        past: h.past.slice(0, -1),
        future: [{ formData, exhibits }, ...h.future].slice(0, HISTORY_MAX),
      };
    });
  };
  const redo = () => {
    setHistory(h => {
      if (!h.future.length) return h;
      const next = h.future[0];
      _setFormData(next.formData);
      _setExhibits(next.exhibits);
      return {
        past: [...h.past, { formData, exhibits }].slice(-HISTORY_MAX),
        future: h.future.slice(1),
      };
    });
  };

  // Global keyboard shortcuts. Skip when focus is inside an editable input
  // so the browser's native character-level undo still works inside cells.
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const ae = document.activeElement;
      const isEditable = ae && (
        ae.isContentEditable ||
        ae.tagName === 'INPUT' ||
        ae.tagName === 'TEXTAREA' ||
        ae.tagName === 'SELECT'
      );
      if (isEditable) return; // browser handles it natively
      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, exhibits, history]);

  // ---- existing versions for the chosen project ----
  const [existingVersions, setExistingVersions] = useState([]);

  // ---- UX state ----
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfPath, setPdfPath] = useState(null);
  const [pdfFileName, setPdfFileName] = useState(null);

  // ---- bootstrap: load projects + company, then restore any active draft ----
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    (async () => {
      try {
        setLoadingProjects(true);
        const [ps, co] = await Promise.all([api.listProjects(), api.companySettings()]);
        setProjects(ps);
        setCompany(co);

        // Restore the last-opened draft (if any) so navigating away and back
        // doesn't lose your place. The actual contract content lives in Mongo;
        // localStorage only holds the pointer.
        const savedId = localStorage.getItem(DRAFT_KEY);
        if (savedId) {
          try {
            const c = await api.get(savedId);
            // Resolve the project from the already-loaded list so the picker
            // shows it as the selected pill without a second fetch.
            const proj = ps.find(p => p._id === (c.project?._id || c.project)) || null;
            setContractId(c._id);
            setContractType(c.type || 'HIC');
            setVersion(c.version || '1');
            setEffectiveDate(c.effectiveDate ? new Date(c.effectiveDate).toISOString().split('T')[0] : todayISO());
            setContractPrice(c.contractPrice || 0);
            // Raw setters here — bootstrap rehydration shouldn't push to the
            // undo stack (nothing to undo yet, this is the initial state).
            _setFormData(migrateFormData(c.formData) || { sections: [] });
            _setExhibits(c.exhibits || []);
            setStatus(c.status || 'Draft');
            setPdfPath(c.dropboxUnsignedPath || null);
            setPdfFileName(fileNameFromPath(c.dropboxUnsignedPath));
            // Existing draft loaded from DB → future edits should autosave
            hasUserEditedRef.current = true;
            setSelectedProject(proj);
          } catch (err) {
            // Draft was deleted, or belongs to a different user (403). Clear
            // the stale pointer so we don't keep retrying on every mount.
            const code = err.response?.status;
            if (code === 404 || code === 403) {
              localStorage.removeItem(DRAFT_KEY);
            } else {
              console.warn('Could not restore active draft', err);
            }
          }
        }
      } catch (err) {
        console.error('Failed to bootstrap contract page', err);
      } finally {
        setLoadingProjects(false);
      }
    })();
  }, [navigate]);

  // ---- when selected project changes: load existing versions + seed template ----
  // /projects already populates customer server-side, so we don't need a second fetch.
  useEffect(() => {
    if (!projectId) {
      setExistingVersions([]);
      return;
    }
    (async () => {
      try {
        const list = await api.list(projectId);
        setExistingVersions(list || []);
        // Auto-fill contract price from the project's stored value, but only
        // if the user hasn't already typed one in (avoid clobbering an edit).
        setContractPrice(prev => prev || selectedProject?.value || 0);

        // Already actively editing a draft? Don't touch anything.
        if (contractId) return;

        // Auto-resume: if this project has any in-progress Draft owned by the
        // current user, load the most recent one instead of creating a new one.
        // (list comes back sorted createdAt desc; status filter happens here.)
        const existingDraft = (list || []).find(c => c.status === 'Draft');
        if (existingDraft) {
          try {
            const full = await api.get(existingDraft._id);
            setContractId(full._id);
            setVersion(full.version || '1');
            setEffectiveDate(full.effectiveDate ? new Date(full.effectiveDate).toISOString().split('T')[0] : todayISO());
            setContractPrice(full.contractPrice || 0);
            _setFormData(migrateFormData(full.formData) || { sections: [] });
            _setExhibits(full.exhibits || []);
            setHistory({ past: [], future: [] });
            setStatus(full.status || 'Draft');
            setPdfPath(full.dropboxUnsignedPath || null);
            setPdfFileName(fileNameFromPath(full.dropboxUnsignedPath));
            localStorage.setItem(DRAFT_KEY, full._id);
            // Existing draft = subsequent edits should auto-save normally
            hasUserEditedRef.current = true;
            return;
          } catch (resumeErr) {
            console.warn('Could not auto-resume existing draft, falling back to template seed', resumeErr);
            // fall through to template seed
          }
        }

        // No existing draft → seed HIC defaults. Raw setters + clear the edit
        // flag so the seed alone doesn't trigger an autosave.
        const t = HICTemplate();
        _setFormData(t.formData);
        _setExhibits(t.exhibits);
        hasUserEditedRef.current = false;
      } catch (err) {
        console.error('Failed to load contract versions for project', err);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // ---- project search filter (matches CreateInvoice / CreateQuote pattern) ----
  // Returns matches by PO #, title, or customer name. Empty search => no results.
  const filteredProjects = useMemo(() => {
    if (!projectSearch) return [];
    const q = projectSearch.toLowerCase();
    return projects.filter(p =>
      (p.poNumber || '').toString().toLowerCase().includes(q) ||
      (p.title || '').toLowerCase().includes(q) ||
      (p.customer?.name || '').toLowerCase().includes(q)
    );
  }, [projects, projectSearch]);

  // ---- auto-save (debounced) ----
  // We only auto-save once we have a contract id (created on first edit).
  // If no id yet, we lazily create one as soon as the user touches anything.
  const hasMaterialChange = useMemo(() => {
    // crude: only auto-save if we have a project + non-empty formData
    return !!(projectId && (formData?.sections?.length || exhibits?.length));
  }, [projectId, formData, exhibits]);

  const lazyCreateDraft = async () => {
    if (contractId) return contractId;
    try {
      const created = await api.create({
        project: projectId,
        type: contractType,
        version,
        effectiveDate,
        contractPrice,
        formData,
        exhibits,
      });
      setContractId(created._id);
      setStatus(created.status || 'Draft');
      // Remember we're editing this draft, so navigating away and back resumes
      // right where we left off (full content is in MongoDB, this is just the
      // pointer).
      localStorage.setItem(DRAFT_KEY, created._id);
      return created._id;
    } catch (err) {
      console.error('Could not create draft', err);
      throw err;
    }
  };

  useDebouncedEffect(() => {
    const save = async () => {
      if (!hasMaterialChange) return;
      // Skip auto-save until the user has actually edited something. Otherwise
      // just landing on the page (which seeds the template) would create a
      // Mongo Draft record every time, polluting the existing-versions list.
      if (!hasUserEditedRef.current) return;
      setSaving(true);
      try {
        const id = await lazyCreateDraft();
        if (!id) return;
        await api.update(id, {
          type: contractType,
          version,
          effectiveDate,
          contractPrice,
          formData,
          exhibits,
        });
        setSavedAt(new Date());
      } catch (err) {
        console.error('Auto-save error', err);
      } finally {
        setSaving(false);
      }
    };
    save();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractType, version, effectiveDate, contractPrice, formData, exhibits], 1500);

  // ---- derived ----
  const ownerName = customer?.name
    || [customer?.firstName, customer?.middleName, customer?.lastName].filter(Boolean).join(' ')
    || '';
  const jobsite = project?.jobSiteAddress
    ? [project.jobSiteAddress.street, project.jobSiteAddress.city, project.jobSiteAddress.state, project.jobSiteAddress.zip].filter(Boolean).join(', ')
    : '';

  // ---- section editors ----
  // Stable section id helper (local — keeps Contract.js self-contained)
  const newSecId = () => `sec_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

  const updateSection = (id, patch) => {
    setFormData(fd => ({
      ...fd,
      sections: (fd.sections || []).map(s => s.id === id ? { ...s, ...patch } : s),
    }));
  };
  const removeSection = (id) => {
    setFormData(fd => ({ ...fd, sections: (fd.sections || []).filter(s => s.id !== id) }));
  };
  const moveSection = (id, delta) => {
    setFormData(fd => {
      const arr = (fd.sections || []).slice();
      const i = arr.findIndex(s => s.id === id);
      if (i === -1) return fd;
      const j = i + delta;
      if (j < 0 || j >= arr.length) return fd;
      const [item] = arr.splice(i, 1);
      arr.splice(j, 0, item);
      return { ...fd, sections: arr };
    });
    // Scroll the moved section back into view so the user can SEE that the
    // swap happened (without this, the move feels like "nothing happened"
    // because the swapped neighbor ends up where your cursor was).
    setTimeout(() => {
      const el = document.querySelector(`[data-sec-id="${id}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 30);
  };
  const insertSection = (anchorId, where /* 'above' | 'below' | 'end' */, kind /* 'header' | 'table' */) => {
    const blank = kind === 'header'
      ? { id: newSecId(), type: 'header', heading: 'New section', html: '<p></p>' }
      : { id: newSecId(), type: 'table',  heading: 'New table', columns: [
            { header: 'Column 1', width: 1 },
            { header: 'Column 2', width: 1 },
            { header: 'Column 3', width: 1 },
          ], rows: [['', '', ''], ['', '', '']] };
    setFormData(fd => {
      const arr = (fd.sections || []).slice();
      if (where === 'end' || !anchorId) {
        arr.push(blank);
      } else {
        const i = arr.findIndex(s => s.id === anchorId);
        if (i === -1) arr.push(blank);
        else arr.splice(where === 'above' ? i : i + 1, 0, blank);
      }
      return { ...fd, sections: arr };
    });
  };

  // Pre-walk the sections to compute the auto-number of each header. Tables
  // don't advance the counter. Section 1 is "List of Documents" (pinned), so
  // header numbering starts at 2. Returns a Map<sectionId, number>.
  const headerNumberById = (() => {
    const map = {};
    let n = 2;
    for (const s of (formData.sections || [])) {
      if (s.type === 'header') { map[s.id] = n++; }
    }
    return map;
  })();

  const renderSection = (sec, idx, all) => {
    const canMoveUp = idx > 0;
    const canMoveDown = idx < all.length - 1;
    const sharedControls = {
      onDelete: () => {
        if (!window.confirm(`Delete this section${sec.heading ? ': "' + sec.heading + '"' : ''}? Undo will bring it back.`)) return;
        removeSection(sec.id);
      },
      onInsertAbove: () => insertSection(sec.id, 'above', sec.type === 'table' ? 'table' : 'header'),
      onInsertBelow: () => insertSection(sec.id, 'below', sec.type === 'table' ? 'table' : 'header'),
      onMoveUp: () => moveSection(sec.id, -1),
      onMoveDown: () => moveSection(sec.id, +1),
      canMoveUp,
      canMoveDown,
    };

    switch (sec.type) {
      case 'header':
        return (
          <HeaderSection
            key={sec.id}
            number={headerNumberById[sec.id]}
            section={sec}
            exhibits={exhibits}
            onChange={(next) => updateSection(sec.id, next)}
            {...sharedControls}
          />
        );
      case 'table':
        return (
          <div key={sec.id} data-sec-id={sec.id} className="group rounded-xl border border-transparent hover:border-zinc-800 hover:bg-gray-950/30 transition-colors -mx-3 px-3 py-2 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                <EditableHeading
                  value={sec.heading}
                  onChange={(heading) => updateSection(sec.id, { heading })}
                />
                {sec.regulated && <RegulatedBadge />}
              </div>
              <SectionControls {...sharedControls} />
            </div>
            <ContractTableEditor
              value={{ columns: sec.columns || [], rows: sec.rows || [] }}
              onChange={(t) => updateSection(sec.id, { columns: t.columns, rows: t.rows })}
            />
          </div>
        );
      default:
        return null;
    }
  };

  // ----- Add Section UI shown at the bottom of the body -----
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const AddSectionMenu = () => (
    <div className="pt-2">
      <div className="relative inline-block">
        <button
          type="button"
          onClick={() => setAddSectionOpen(o => !o)}
          className="px-3 py-2 text-xs border border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 rounded-lg inline-flex items-center gap-2"
        >
          <span className="text-base leading-none">+</span> Add section
        </button>
        {addSectionOpen && (
          // Open UPWARD — this menu sits at the bottom of the body card and
          // would otherwise be clipped by the page's overflow-hidden container.
          <div className="absolute left-0 bottom-10 z-30 bg-zinc-900 border border-zinc-700 rounded-md py-1 min-w-[200px] shadow-xl">
            <button
              type="button"
              onClick={() => { insertSection(null, 'end', 'header'); setAddSectionOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800"
            >
              Header section (numbered)
            </button>
            <button
              type="button"
              onClick={() => { insertSection(null, 'end', 'table'); setAddSectionOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800"
            >
              Table section
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ---- actions ----
  const handleGenerate = async () => {
    setBusy(true);
    try {
      const id = await lazyCreateDraft();
      await api.update(id, { type: contractType, version, effectiveDate, contractPrice, formData, exhibits });
      const res = await api.generatePdf(id);
      setPdfPath(res.dropboxPath);
      setPdfFileName(res.fileName);
      setStatus('Sent');
      // No auto-download here per the simplified flow — generate uploads to
      // Dropbox and flips status; the user can hit Preview if they want to
      // see the PDF, or grab it from Dropbox directly.
    } catch (err) {
      console.error('Generate failed', err);
      alert('Could not generate PDF: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusy(false);
    }
  };

  // Preview opens the rendered PDF in a new browser tab. Implicitly saves the
  // current state first (so what you see in the PDF matches what's on screen).
  // No Dropbox upload, no status change — just bytes in a new tab.
  const handlePreview = async () => {
    if (!selectedProject) {
      alert('Pick a project first.');
      return;
    }
    setBusy(true);
    try {
      const id = await lazyCreateDraft();
      await api.update(id, { type: contractType, version, effectiveDate, contractPrice, formData, exhibits });
      const blob = await api.previewPdf(id);
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener');
      // Revoke after a minute — gives the new tab time to render the PDF.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      console.error('Preview failed', err);
      alert('Could not preview PDF: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusy(false);
    }
  };

  const handleUploadSigned = async (file) => {
    if (!contractId) return;
    setBusy(true);
    try {
      await api.uploadSigned(contractId, file);
      setStatus('Signed');
    } catch (err) {
      console.error('Signed upload failed', err);
      alert('Upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = async () => {
    if (!contractId) {
      navigate('/billing');
      return;
    }
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    try {
      await api.remove(contractId);
      localStorage.removeItem(DRAFT_KEY);
      navigate('/billing');
    } catch (err) {
      alert('Delete failed');
    }
  };

  const loadExistingVersion = async (id) => {
    try {
      const c = await api.get(id);
      setContractId(c._id);
      setContractType(c.type || 'HIC');
      setVersion(c.version || '1');
      setEffectiveDate(c.effectiveDate ? new Date(c.effectiveDate).toISOString().split('T')[0] : todayISO());
      setContractPrice(c.contractPrice || 0);
      // Raw — loading another version isn't an undoable action
      _setFormData(migrateFormData(c.formData) || { sections: [] });
      _setExhibits(c.exhibits || []);
      // Reset history when switching versions so undo can't bleed across them
      setHistory({ past: [], future: [] });
      setStatus(c.status || 'Draft');
      setPdfPath(c.dropboxUnsignedPath || null);
      // Loaded an existing version → enable autosave for future edits
      hasUserEditedRef.current = true;
      setPdfFileName(fileNameFromPath(c.dropboxUnsignedPath));
      // Reopening an existing version makes it the active draft for next mount.
      localStorage.setItem(DRAFT_KEY, c._id);
    } catch (err) {
      alert('Could not load that version');
    }
  };

  // ---- render ----
  return (
    <div
      // Invisible while we restore scroll, so the user doesn't see the page
      // render at top → jump to saved position. Keep pointer-events disabled
      // too so a tab/keystroke during the brief hide can't trigger anything.
      style={{
        opacity: isRestoringScroll ? 0 : 1,
        pointerEvents: isRestoringScroll ? 'none' : 'auto',
        transition: 'opacity 80ms ease-out',
      }}
      className="h-screen flex flex-col bg-gray-950 text-zinc-100 overflow-hidden">
      {/* TOP BAR — an ordinary (non-scrolling) flex child. The scrollable
          content lives in the sibling container below, so this bar stays put
          while you scroll and content can never appear above or behind it. */}
      <div
        className="shrink-0 px-8 pt-4 pb-4 bg-gray-950 flex items-start justify-between border-b border-gray-800">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Create Contract</h1>
        </div>
        <div className="flex items-center gap-3">
          {/* Undo / Redo — disabled when their stack is empty. Cmd/Ctrl+Z
              and Cmd/Ctrl+Shift+Z (or Cmd/Ctrl+Y) are also wired up via the
              global keydown listener. */}
          <div className="flex items-center gap-1 border-r border-zinc-800 pr-3 mr-1">
            <button
              type="button"
              onClick={undo}
              disabled={!history.past.length}
              className="p-1.5 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent rounded transition-colors"
              title="Undo (Cmd/Ctrl+Z)"
              aria-label="Undo"
            >
              <Undo2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={redo}
              disabled={!history.future.length}
              className="p-1.5 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent rounded transition-colors"
              title="Redo (Cmd/Ctrl+Shift+Z)"
              aria-label="Redo"
            >
              <Redo2 className="w-4 h-4" />
            </button>
          </div>
          <StatusPill value={saving ? 'Saving' : (status || 'Draft')} />
          {savedAt && !saving && (
            <span className="text-[11px] text-zinc-500">last saved {formatStamp(savedAt)}</span>
          )}
          <LifecycleControls
            status={status}
            busy={busy}
            onPreview={handlePreview}
            onGenerate={handleGenerate}
            onUploadSigned={handleUploadSigned}
            onDiscard={handleDiscard}
          />
        </div>
      </div>

      {/* SCROLLABLE CONTENT — this container scrolls, not the window. */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-8 pt-6 pb-8 space-y-6">
      {/* SETUP CARD */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-5 relative">
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1">Project</label>

            {/* When a project is selected, show a compact pill with a clear button.
                When nothing is selected, show the search input + live results list. */}
            {selectedProject ? (
              <div className="flex items-center justify-between gap-2 bg-gray-950/50 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100">
                <span className="truncate">
                  <span className="text-indigo-300 font-medium">
                    {String(selectedProject.poNumber || '0000').padStart(4, '0')}
                  </span>
                  <span className="text-zinc-500"> — </span>
                  {selectedProject.title}
                  <span className="text-zinc-500"> · </span>
                  <span className="text-zinc-400">{selectedProject.customer?.name || 'No customer'}</span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    // Switching projects means starting a fresh draft. The
                    // previous draft stays in Mongo (you can re-open it from
                    // 'existing versions' on the project that owns it).
                    setSelectedProject(null);
                    setProjectSearch('');
                    setContractId(null);
                    // Raw + reset history — fresh-draft reset isn't undoable
                    _setFormData({ sections: [] });
                    _setExhibits([]);
                    setHistory({ past: [], future: [] });
                    setStatus('Draft');
                    setPdfPath(null);
                    // Fresh start — autosave should wait for a real edit again
                    hasUserEditedRef.current = false;
                    setPdfFileName(null);
                    setVersion('1');
                    setEffectiveDate(todayISO());
                    setContractPrice(0);
                    localStorage.removeItem(DRAFT_KEY);
                  }}
                  className="text-zinc-500 hover:text-zinc-200"
                  aria-label="Change project"
                  title="Change project"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" aria-hidden />
                  <input
                    type="text"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    disabled={loadingProjects}
                    placeholder={loadingProjects ? 'Loading projects…' : 'Find by PO #, project title, or customer name…'}
                    className="w-full bg-gray-950/50 border border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-indigo-500 disabled:opacity-60"
                  />
                </div>

                {projectSearch && !loadingProjects && (
                  <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl">
                    {filteredProjects.length === 0 ? (
                      <div className="px-3 py-2.5 text-xs text-zinc-500">No matches</div>
                    ) : (
                      filteredProjects.map((p) => (
                        <button
                          key={p._id}
                          type="button"
                          onClick={() => {
                            setSelectedProject(p);
                            setProjectSearch('');
                            setContractId(null);
                          }}
                          className="block w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-800 border-b border-zinc-800 last:border-b-0"
                        >
                          <span className="text-indigo-300 font-medium">
                            {String(p.poNumber || '0000').padStart(4, '0')}
                          </span>
                          <span className="text-zinc-500"> — </span>
                          {p.title}
                          <span className="text-zinc-500"> · </span>
                          <span className="text-zinc-400">{p.customer?.name || 'No customer'}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="md:col-span-4">
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1">Contract type</label>
            <ContractTypeDropdown value={contractType} onChange={setContractType} />
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1">Version</label>
            <input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              className="w-full bg-gray-950/50 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-indigo-500"
              placeholder="1"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1">Effective</label>
            <input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              className="w-full bg-gray-950/50 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-indigo-500"
            />
          </div>
        </div>

        {project && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 p-3 rounded-lg bg-gray-950/40 border border-zinc-800/60">
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">Owner</div>
              <div className="text-sm text-zinc-200">{ownerName || '—'}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">Job site</div>
              <div className="text-sm text-zinc-200">{jobsite || '—'}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">Contract price</div>
              <input
                type="number"
                value={contractPrice}
                onChange={(e) => setContractPrice(Number(e.target.value || 0))}
                className="w-full bg-gray-950/50 border border-zinc-800 rounded-lg px-2 py-1 text-sm text-zinc-100 focus:outline-none focus:border-indigo-500"
              />
              <div className="text-[11px] text-zinc-500 mt-1">{fmtMoney(contractPrice)}</div>
            </div>
          </div>
        )}

        {existingVersions.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-zinc-400">
            <span className="text-zinc-500 mr-1">existing versions for this project:</span>
            {existingVersions.slice(0, 8).map(v => (
              // Each pill = click to load that version. Trash icon on the right
              // deletes the version after a confirm. Active version (currently
              // loaded) gets an indigo bar so you can tell which one you're on.
              <span
                key={v._id}
                className={`inline-flex items-stretch border rounded overflow-hidden ${contractId === v._id ? 'border-indigo-700' : 'border-zinc-800'}`}
              >
                <button
                  type="button"
                  onClick={() => loadExistingVersion(v._id)}
                  className={`px-2 py-1 text-left hover:bg-zinc-800 hover:text-zinc-100 ${contractId === v._id ? 'bg-indigo-950/40 text-indigo-200' : ''}`}
                  title="Load this version"
                >
                  {v.type} v{v.version}
                  <span className="text-zinc-600"> · {v.status}</span>
                  <span className="text-zinc-600 ml-2">· {formatStamp(v.updatedAt || v.createdAt)}</span>
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(`Delete ${v.type} v${v.version}? This cannot be undone.`)) return;
                    try {
                      await api.remove(v._id);
                      // Pull the latest list so the deleted version disappears
                      const fresh = await api.list(projectId);
                      setExistingVersions(fresh || []);
                      // If we just deleted the active draft, reset the page
                      if (contractId === v._id) {
                        setContractId(null);
                        _setFormData({ sections: [] });
                        _setExhibits([]);
                        setHistory({ past: [], future: [] });
                        setStatus('Draft');
                        setPdfPath(null);
                        setPdfFileName(null);
                        localStorage.removeItem(DRAFT_KEY);
                        hasUserEditedRef.current = false;
                      }
                    } catch (err) {
                      alert('Delete failed: ' + (err.response?.data?.error || err.message));
                    }
                  }}
                  className="px-2 border-l border-zinc-800 text-zinc-500 hover:text-red-300 hover:bg-red-900/20"
                  title="Delete this version"
                  aria-label="Delete this version"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {!projectId && (
        <div className="rounded-xl border border-zinc-800 bg-gray-900/40 p-8 text-center text-zinc-400">
          Pick a project to begin. Owner info, job-site address and PO number auto-fill from the project record.
        </div>
      )}

      {projectId && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* MAIN BODY */}
          <div className="lg:col-span-2 space-y-4">
            {/* CENTRAL VIEW — Contract Body card (with the page-1 list of
                documents merged in as section 1) or the active exhibit editor. */}
            {activeView === 'body' ? (
              <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">Contract Body</h2>
                  <span className="text-[11px] text-zinc-500">click any paragraph to edit · tables are always editable</span>
                </div>

                {/* 1. List of pages to be incorporated — click a row to open
                    that exhibit's editor; click the active row to return here. */}
                <div className="rounded-lg border border-zinc-800/50 bg-gray-950/20 p-3">
                  <h3 className="text-sm font-semibold text-white">1. List of pages to be incorporated into this contract</h3>
                  <div className="mt-2 space-y-1 text-[13px]">
                    {exhibits.length === 0 && <div className="text-zinc-500">No exhibits yet. Add one in the panel on the right.</div>}
                    {exhibits.map((ex, i) => {
                      const isActive = activeView === ex.exhibitId;
                      return (
                        <button
                          key={ex.exhibitId}
                          type="button"
                          onClick={() => setActiveView(isActive ? 'body' : ex.exhibitId)}
                          className={`group w-full text-left flex items-center gap-2 px-2 py-1 rounded border-l-2 transition-colors ${
                            isActive
                              ? 'border-indigo-500 bg-indigo-950/30 text-zinc-100'
                              : 'border-transparent text-zinc-300 hover:bg-zinc-900/60 hover:border-zinc-700'
                          }`}
                          title={isActive ? 'Click to return to Contract Body' : 'Click to open this exhibit'}
                        >
                          <span className="text-indigo-300 font-medium">EXHIBIT {letterOf(i)}</span>
                          <span className="text-zinc-600">—</span>
                          <span className="truncate">{ex.title || '(untitled)'}</span>
                          <span className="ml-auto text-[10px] text-zinc-500">
                            {isActive
                              ? <span className="group-hover:text-red-300 transition-colors">× close</span>
                              : <span className="opacity-0 group-hover:opacity-100 transition-opacity">→ open</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Numbered body sections (2, 3, …) — subtly separated by faint dividers. */}
                <div className="divide-y divide-zinc-800/40 [&>*+*]:pt-3">
                  {(formData.sections || []).map((sec, i, all) => renderSection(sec, i, all))}
                </div>
                <AddSectionMenu />
              </div>
            ) : (
              <ExhibitDetail
                contractId={contractId}
                exhibit={exhibits.find(e => e.exhibitId === activeView)}
                exhibitIndex={exhibits.findIndex(e => e.exhibitId === activeView)}
                company={company}
                contractor={{
                  nameLine: `${company?.signatory?.name || ''}${company?.signatory?.title ? ' - ' + company.signatory.title : ''}`.trim() || company?.companyName || '',
                  addressLine: company ? [company.address?.street, company.address?.city, company.address?.state, company.address?.zip].filter(Boolean).join(', ') : '',
                }}
                effectiveDate={effectiveDate}
                onChange={(next) => setExhibits(exhibits.map(e => e.exhibitId === activeView ? next : e))}
                onClose={() => setActiveView('body')}
              />
            )}

            {/* PDF PATH (after generate) */}
            {pdfPath && (
              <div className="bg-emerald-950/30 border border-emerald-800/40 rounded-xl p-3 text-xs text-emerald-200 flex items-center justify-between gap-3">
                <span>
                  PDF saved to Dropbox: <span className="font-mono text-emerald-300">{pdfPath}</span>
                </span>
                <button
                  type="button"
                  onClick={handlePreview}
                  className="text-emerald-200 hover:text-emerald-100 underline underline-offset-2"
                >
                  Open preview
                </button>
              </div>
            )}
          </div>

          {/* SIDEBAR — EXHIBITS (pinned; the list scrolls inside its own box) */}
          <div className="lg:col-span-1">
            <div className="sticky top-0 max-h-[calc(100vh-6rem)] flex flex-col bg-gray-900 rounded-2xl border border-gray-800/50 p-5">
              <ExhibitsPanel
                exhibits={exhibits}
                contractId={contractId}
                onChangeExhibits={setExhibits}
              />
            </div>
          </div>
        </div>
      )}
      </div>{/* /SCROLLABLE CONTENT */}
    </div>
  );
}
